import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import MuiSearchSelect from "../../../../components/basicComponent/MuiSearchSelect";
import { MuiInput } from "../../../../components/basicComponent/MuiInput";
import MuiDatePicker from "../../../../components/basicComponent/MuiDatePicker";
import MuiButton from "../../../../components/basicComponent/MuiButton";
import { useFormik } from "formik";
import dayjs from "dayjs";
import { UtilService } from "../../../../services/shared/UtilService";
import { msgTypes } from "../../../../constants/msgTypes";
import isSameOrAfter from "dayjs/plugin/isSameOrAfter";
import isSameOrBefore from "dayjs/plugin/isSameOrBefore";

dayjs.extend(isSameOrAfter);
dayjs.extend(isSameOrBefore);

interface ChildInfo {
  NumberOfChild: number;
  childAge: number[];
}

interface RoomInfo {
  NumberOfAdults: number;
  Child: ChildInfo;
}

interface RoomsData {
  Count: string;
  Room: RoomInfo[];
}

interface CityOption {
  label: string;
  value: string;
}

interface HotelHeaderCardProps {
  city?: string;
  checkIn?: string;
  checkOut?: string;
  roomsData?: RoomsData;
}

const HotelHeaderCard: React.FC<HotelHeaderCardProps> = ({
  city = "",
  checkIn = "",
  checkOut = "",
  roomsData = {
    Count: "1 Room, 1 Guest",
    Room: [
      {
        NumberOfAdults: 1,
        Child: { NumberOfChild: 0, childAge: [] },
      },
    ],
  },
}) => {
  const [isPopupOpen, setIsPopupOpen] = useState(false);
  const [citySearch, setCitySearch] = useState<string>(city);
  const [cityOptions, setCityOptions] = useState<CityOption[]>([]);
  const navigate = useNavigate();

  const openPopup = () => setIsPopupOpen(true);

  const formik = useFormik({
    initialValues: {
      city: city ? { label: city, value: city } : { label: "", value: "" },
      CheckInDate: checkIn,
      CheckOutDate: checkOut,
      rooms: roomsData,
    },
    onSubmit: (values) => {
      const payload = {
        Chain: 0,
        CheckInDate: values.CheckInDate,
        CheckOutDate: values.CheckOutDate,
        City: values.city.label,
        CityName: values.city.label,
        EMTAuthentication: {
          AgentCode: 14,
          IPAddress: " AgentCode: 1",
          Password: "hotelapitest6NKHbRNN4pSD9G2H",
          UserName: "HotelAPIUserTest",
        },
        IsEMTAPI: true,
        Nights: 1,
        rooms: {
          Count: values.rooms.Room.length,
          Room: values.rooms.Room,
        },
        username: "HotelAPIUserTest",
      };

      navigate("/hotellist", { state: { payload } });
    },
    enableReinitialize: true,
  });

  useEffect(() => {
    const delayDebounce = setTimeout(async () => {
      if (citySearch.length >= 3 && citySearch !== formik.values.city?.label) {
        const response = await UtilService.fetchCities(citySearch);
        if (response?.statusMsg === msgTypes.SUCCESS.msg) {
          const formatted = response.data.map((item: any) => ({
            label: `${item.city}, ${item.country}`,
            value: `${item.city}, ${item.country}`,
          }));
          setCityOptions(formatted);
        }
      }
    }, 400);
    return () => clearTimeout(delayDebounce);
  }, [citySearch]);

  const totalGuests = formik.values.rooms.Room.reduce(
    (sum, room) => sum + room.NumberOfAdults + room.Child.NumberOfChild,
    0
  );
  const guestSummary = `${formik.values.rooms.Room.length} Room${
    formik.values.rooms.Room.length > 1 ? "s" : ""
  }, ${totalGuests} Guest${totalGuests !== 1 ? "s" : ""}`;

  const SelectTravellers = () => {
    const handleIncrement = (
      type: "adults" | "children",
      roomIndex: number
    ) => {
      const room = formik.values.rooms.Room[roomIndex];
      const total = room.NumberOfAdults + room.Child.NumberOfChild;

      if (type === "adults" && room.NumberOfAdults < 12 && total < 12) {
        formik.setFieldValue(
          `rooms.Room[${roomIndex}].NumberOfAdults`,
          room.NumberOfAdults + 1
        );
      }

      if (type === "children" && room.Child.NumberOfChild < 12 && total < 12) {
        formik.setFieldValue(
          `rooms.Room[${roomIndex}].Child.NumberOfChild`,
          room.Child.NumberOfChild + 1
        );
      }
    };

    const handleDecrement = (
      type: "adults" | "children",
      roomIndex: number
    ) => {
      const room = formik.values.rooms.Room[roomIndex];
      if (type === "adults" && room.NumberOfAdults > 1) {
        formik.setFieldValue(
          `rooms.Room[${roomIndex}].NumberOfAdults`,
          room.NumberOfAdults - 1
        );
      }
      if (type === "children" && room.Child.NumberOfChild > 0) {
        formik.setFieldValue(
          `rooms.Room[${roomIndex}].Child.NumberOfChild`,
          room.Child.NumberOfChild - 1
        );
      }
    };

    const handleAddRoom = () => {
      const current = formik.values.rooms.Room;
      if (current.length < 12) {
        const newRoom: RoomInfo = {
          NumberOfAdults: 1,
          Child: {
            NumberOfChild: 0,
            childAge: [],
          },
        };
        formik.setFieldValue("rooms.Room", [...current, newRoom]);
      }
    };

    const handleRemoveRoom = (roomIndex: number) => {
      const updatedRooms = [...formik.values.rooms.Room];
      updatedRooms.splice(roomIndex, 1);
      formik.setFieldValue("rooms.Room", updatedRooms);
    };

    const handleDone = () => {
      const rooms = formik.values.rooms.Room;
      const totalAdults = rooms.reduce(
        (sum, room) => sum + room.NumberOfAdults,
        0
      );
      const totalChildren = rooms.reduce(
        (sum, room) => sum + room.Child.NumberOfChild,
        0
      );
      const totalGuests = totalAdults + totalChildren;
      const roomCount = rooms.length;

      const summary = `${roomCount} Room${
        roomCount > 1 ? "s" : ""
      }, ${totalGuests} Guest${totalGuests !== 1 ? "s" : ""}`;
      formik.setFieldValue("rooms.Count", summary);
      setIsPopupOpen(false);
    };

    return (
      <div className="select-travellers-container bg-white p-3 rounded shadow absolute">
        <h3 className="text-lg mb-1 font-semibold">
          {formik.values.rooms.Room.length} Room
          {formik.values.rooms.Room.length > 1 ? "s" : ""},{" "}
          {formik.values.rooms.Room.reduce(
            (total, room) =>
              total + room.NumberOfAdults + room.Child.NumberOfChild,
            0
          )}{" "}
          Guests
        </h3>

        <div className="overflow-auto" style={{ maxHeight: "214px" }}>
          {formik.values.rooms.Room.map((room, roomIndex) => (
            <div key={roomIndex} className="room-container mb-3">
              <h4>Room {roomIndex + 1}</h4>
              <div className="d-flex justify-content-between">
                <div>
                  <label>Adults</label>
                  <div className="d-flex gap-2">
                    <button
                      type="button"
                      className="w-5 h-5 rounded-full flex items-center justify-center border"
                      onClick={() => handleDecrement("adults", roomIndex)}
                    >
                      <span>-</span>
                    </button>
                    <span>{room.NumberOfAdults}</span>
                    <button
                      type="button"
                      className=" w-5 h-5 rounded-full flex items-center justify-center border"
                      onClick={() => handleIncrement("adults", roomIndex)}
                    >
                      +
                    </button>
                  </div>
                </div>
                <div>
                  <label>Children</label>
                  <div className="d-flex gap-2">
                    <button
                      type="button"
                      className="w-5 h-5 rounded-full flex items-center justify-center border"
                      onClick={() => handleDecrement("children", roomIndex)}
                    >
                      -
                    </button>
                    <span>{room.Child.NumberOfChild}</span>
                    <button
                      type="button"
                      className="w-5 h-5 rounded-full flex items-center justify-center border"
                      onClick={() => handleIncrement("children", roomIndex)}
                    >
                      +
                    </button>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>

        <div
          className="d-flex align-items-center justify-content-between py-3"
          style={{ position: "sticky", bottom: "0", left: "0", width: "100%" }}
        >
          <button type="button" className="add-room" onClick={handleAddRoom}>
            Add
          </button>
          {formik.values.rooms.Room.length > 1 && (
            <button
              className="remove-room"
              onClick={() =>
                handleRemoveRoom(formik.values.rooms.Room.length - 1)
              }
            >
              Remove
            </button>
          )}
        </div>
        <button type="button" className="done-btn" onClick={handleDone}>
          Done
        </button>
      </div>
    );
  };

  return (
    <div className="flight-search-contain">
      <form className="flight-search-form" onSubmit={formik.handleSubmit}>
        <div className="container-fluid">
          <div className="row mb-2">
            <div className="col-lg-4 col-md-3 col-sm-6">
              <label className="py-2">
                City name, Location or Specific hotel
              </label>
              <MuiSearchSelect
                placeholder="City"
                options={[
                  ...(formik.values.city.label ? [formik.values.city] : []),
                  ...cityOptions,
                ]}
                value={formik.values.city.value}
                inputValue={citySearch}
                onInputChange={(val) => setCitySearch(val)}
                onChange={(value, id, selectedOption) => {
                  formik.setFieldValue("city", selectedOption);
                  setCitySearch(selectedOption?.label || "");
                }}
              />
            </div>
            <div className="col-lg-2 col-md-3 col-sm-6">
              <label className="py-2">Check-In</label>
              <MuiDatePicker
                value={
                  formik.values.CheckInDate
                    ? dayjs(formik.values.CheckInDate)
                    : null
                }
                onChange={(date) => {
                  const newCheckIn = dayjs(date);
                  const currentCheckOut = dayjs(formik.values.CheckOutDate);

                  // Auto-adjust Check-Out if needed
                  if (
                    !currentCheckOut.isValid() ||
                    newCheckIn.isSameOrAfter(currentCheckOut)
                  ) {
                    const adjustedCheckOut = newCheckIn
                      .add(1, "day")
                      .format("YYYY-MM-DD");
                    formik.setFieldValue("CheckOutDate", adjustedCheckOut);
                  }

                  formik.setFieldValue(
                    "CheckInDate",
                    newCheckIn.format("YYYY-MM-DD")
                  );
                }}
              />
            </div>
            <div className="col-lg-2 col-md-3 col-sm-6">
              <label className="py-2">Check-Out</label>
              <MuiDatePicker
                value={
                  formik.values.CheckOutDate
                    ? dayjs(formik.values.CheckOutDate)
                    : null
                }
                onChange={(date) => {
                  const newCheckOut = dayjs(date);
                  const currentCheckIn = dayjs(formik.values.CheckInDate);

                  // Prevent setting Check-Out before or same as Check-In
                  if (
                    !newCheckOut.isValid() ||
                    !newCheckOut.isAfter(currentCheckIn)
                  ) {
                    formik.setFieldError(
                      "CheckOutDate",
                      "Check-out must be after check-in date"
                    );
                    return;
                  }

                  formik.setFieldValue(
                    "CheckOutDate",
                    newCheckOut.format("YYYY-MM-DD")
                  );
                  formik.setFieldError("CheckOutDate", "");
                }}
              />
            </div>
            <div className="col-lg-2 col-md-3 col-sm-6">
              <label className="py-2">Rooms/Guests</label>
              <MuiInput
                placeholder="Rooms & Guests"
                value={guestSummary}
                onClick={openPopup}
                onChange={() => {}}
              />
              {isPopupOpen && (
                <div className="popup-container position-absolute z-10 rounded p-3 mt-1">
                  {SelectTravellers()}
                </div>
              )}
            </div>
            <div className="col-lg-2 col-md-3 col-sm-6 ">
              <div className="flightlist-search-btn">
                <MuiButton
                  style={{ marginLeft: "auto" }}
                  label="Search"
                  variant="contained"
                  size="large"
                  type="submit"
                />
              </div>
            </div>
          </div>
        </div>
      </form>
    </div>
  );
};

export default HotelHeaderCard;
